<template>
  <v-dialog :value="value" max-width="560px" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        Agregar persona
        <v-spacer />
        <v-btn icon aria-label="Cerrar" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <template v-if="matches.length">
          <p class="mb-2">Ya hay alguien parecido en Pipedrive. ¿Es la misma persona?</p>
          <v-list dense class="mb-2">
            <v-list-item v-for="match in matches" :key="match.id">
              <v-list-item-content>
                <v-list-item-title>{{ match.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="match.orgName">{{ match.orgName }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small color="primary" depressed class="use-existing-btn" @click="useExisting(match)">
                  Usar esta persona
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>

        <v-form v-show="!matches.length" ref="form" @submit.prevent="save()">
          <v-text-field
            v-model.trim="form.name"
            label="Nombre"
            :rules="[v => !!v || 'Escribe el nombre']"
            :disabled="saving"
            outlined
            dense
          />
          <v-text-field v-model.trim="form.title" label="Cargo" :disabled="saving" outlined dense />
          <v-text-field v-model.trim="form.email" label="Email" type="email" :disabled="saving" outlined dense />
          <v-text-field v-model.trim="form.linkedinUrl" label="LinkedIn" placeholder="https://www.linkedin.com/in/..." :disabled="saving" outlined dense />
          <org-chart-placement-fields
            v-model="placement"
            :people="managerOptions"
            :areas="areas"
            :disabled="saving"
          />
        </v-form>

        <v-alert v-if="errorText" type="error" dense text class="mt-3 mb-0">{{ errorText }}</v-alert>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-spacer />
        <template v-if="matches.length">
          <v-btn text :disabled="saving" @click="matches = []">Volver</v-btn>
          <v-btn text :loading="saving" class="force-create-btn" @click="save(true)">Crear de todas formas</v-btn>
        </template>
        <template v-else>
          <v-btn text :disabled="saving" @click="close">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="saving" class="save-btn" @click="save()">Guardar</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import OrgChartPlacementFields from './OrgChartPlacementFields.vue';
import { errorMessage, toPlacement } from '../utils/org-chart.utils';

const emptyForm = () => ({ name: '', title: '', email: '', linkedinUrl: '' });

export default {
  name: 'AddPersonModal',
  components: { OrgChartPlacementFields },
  props: {
    value: { type: Boolean, default: false },
    orgId: { type: [Number, String], required: true },
    nodes: { type: Array, default: () => [] },
    unplaced: { type: Array, default: () => [] },
    areas: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      form: emptyForm(),
      placement: { manager: null, area: null },
      matches: [],
      saving: false,
      errorText: ''
    };
  },
  computed: {
    managerOptions() {
      return [...this.nodes, ...this.unplaced];
    }
  },
  watch: {
    value(open) {
      if (open) this.reset();
    }
  },
  methods: {
    reset() {
      this.form = emptyForm();
      this.placement = { manager: null, area: null };
      this.matches = [];
      this.errorText = '';
      this.saving = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() {
      this.$emit('input', false);
    },
    buildPayload(skipDuplicateCheck) {
      const payload = { name: this.form.name };
      ['title', 'email', 'linkedinUrl'].forEach(field => {
        if (this.form[field]) payload[field] = this.form[field];
      });
      const placement = toPlacement(this.placement);
      if (placement) payload.placement = placement;
      if (skipDuplicateCheck) payload.skipDuplicateCheck = true;
      return payload;
    },
    async save(skipDuplicateCheck = false) {
      if (!skipDuplicateCheck && this.$refs.form && !this.$refs.form.validate()) return;
      this.saving = true;
      this.errorText = '';
      const res = await ClientOrgChartsService.createPerson(this.orgId, this.buildPayload(skipDuplicateCheck));
      this.saving = false;
      if (res.status === 'ok') {
        this.$emit('created', { personId: res.data && res.data.personId });
        this.close();
        return;
      }
      if (res.code === 'POSSIBLE_DUPLICATE_PERSON' && res.details && res.details.matches && res.details.matches.length) {
        this.matches = res.details.matches;
        return;
      }
      this.errorText = errorMessage(res, 'No pudimos crear la persona. Intenta de nuevo.');
    },
    useExisting(match) {
      // Payload: { personId, placement } — placement es null si no se eligió jefe.
      this.$emit('use-existing', { personId: match.id, placement: toPlacement(this.placement) });
      this.close();
    }
  }
};
</script>
